import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { BEResponse } from '@qtek/core/api-core';
import { BaseWebsocketMessage } from '@qtek/core/websockets-core';
import { NormalSnackBarData, SnackBarService } from '@qtek/libs/snack-bar-core';
import { ITemplateData, TemplateVersionStage } from '@qtek/shared/models';
import { Observable, pipe, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { TemplateDataService } from './template-data.service';

export interface TemplateDataState {
  templateData: ITemplateData[] | null;
  isTemplateDataLoading: boolean;
  selectedTemplateDataKey: string | null;
  lastPublishTime: number;
  singleTemplateDataByKey: Record<string, ITemplateData>;
}

const templateDataInitialState: TemplateDataState = {
  templateData: null,
  isTemplateDataLoading: false,
  selectedTemplateDataKey: null,
  lastPublishTime: 0,
  singleTemplateDataByKey: {},
};

interface TemplateDataOpMessage extends BaseWebsocketMessage {
  res?: ITemplateData[];
  sts?: Record<string, unknown>;
}

export const TemplateDataStore = signalStore(
  { providedIn: 'root' },
  withState(templateDataInitialState),
  withComputed(store => ({
    selectedTemplateData: computed(() => {
      const list = store.templateData();
      const key = store.selectedTemplateDataKey();
      if (!list || !key) return null;

      return list.find(item => item._cts === key) ?? null;
    }),
    singleTemplateData: computed(
      () => (key: string) => store.singleTemplateDataByKey()[key] ?? null
    ),
  })),
  withMethods(
    (
      store,
      templateDataService = inject(TemplateDataService),
      snackBar = inject(SnackBarService)
    ) => {
      const disconnect$ = new Subject<void>();

      const stopLoading = () =>
        patchState(store, { isTemplateDataLoading: false });

      return {
        connectTemplateDataList: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { isTemplateDataLoading: true })),
            switchMap(id =>
              templateDataService
                .subscribe({ prms: { template: id, auditNames: 'true' } })
                .pipe(
                  takeUntil(disconnect$),
                  tap(({ op, res }: TemplateDataOpMessage) => {
                    switch (op) {
                      case 'query':
                        patchState(store, {
                          templateData: res ? (res as ITemplateData[]) : [],
                          isTemplateDataLoading: false,
                        });
                        break;
                      case 'ins': {
                        if (!res) break;
                        const inserted = res as unknown as ITemplateData;
                        patchState(store, state => ({
                          templateData: [inserted, ...state.templateData],
                          isTemplateDataLoading: false,
                        }));
                        break;
                      }
                      case 'upd': {
                        if (!res) break;
                        const updated = res as unknown as ITemplateData;

                        patchState(store, state => ({
                          templateData: (state.templateData ?? []).map(item =>
                            item._cts === updated._cts ? updated : item
                          ),
                          isTemplateDataLoading: false,
                          ...(updated.versionStage === 'PUBLISHED'
                            ? { lastPublishTime: Date.parse(updated._uts) }
                            : {}),
                        }));

                        if (updated.versionStage === 'PUBLISHED') {
                          snackBar.showSnackBar({
                            type: 'NormalMessage',
                            message: 'GNR_TEMPLATE_VERSION_PUBLISHED',
                          } as NormalSnackBarData);
                        }
                        break;
                      }
                    }
                  })
                )
            )
          )
        ),

        disconnectTemplateData(): void {
          disconnect$.next();
          patchState(store, {
            templateData: null,
            selectedTemplateDataKey: null,
            isTemplateDataLoading: false,
          });
        },

        selectTemplateData(item: ITemplateData): void {
          patchState(store, { selectedTemplateDataKey: item._cts });
        },

        getTemplateData(id: string, version: string): void {
          const key = `${id}.${version}`;
          templateDataService
            .getTemplateData(id, version)
            .subscribe(response => {
              if (!response.res) return;

              patchState(store, state => ({
                singleTemplateDataByKey: {
                  ...state.singleTemplateDataByKey,
                  [key]: response.res as ITemplateData,
                },
              }));
            });
        },

        createTemplateData(
          tplId: string,
          baseDocId?: string,
          body?: unknown
        ): Observable<BEResponse<ITemplateData>> {
          patchState(store, { isTemplateDataLoading: true });
          return templateDataService
            .createTemplateData(tplId, baseDocId, body)
            .pipe(tap({ next: stopLoading, error: stopLoading }));
        },

        updateTemplateData(
          id: string,
          version: string,
          body: unknown,
          versionStage: TemplateVersionStage
        ): Observable<BEResponse<ITemplateData>> {
          return templateDataService
            .updateTemplateData(id, version, body, versionStage)
            .pipe(tap({ next: stopLoading, error: stopLoading }));
        },

        setBaseDocumentReferenceId(
          templateDataCts: string,
          baseDocumentReferenceId: string
        ): void {
          patchState(store, state => ({
            templateData: (state.templateData ?? []).map(item =>
              item._cts === templateDataCts
                ? { ...item, baseDocumentReferenceId }
                : item
            ),
          }));
        },
      };
    }
  )
);
